/**
 * Table primitives — thin Tailwind wrappers around HTML table elements
 * that apply the canonical sizing/spacing from designTokens. Compose
 * them like normal HTML tables:
 *
 *   <Table>
 *     <THead>
 *       <TR>
 *         <TH>Name</TH>
 *         <TH>Status</TH>
 *         <TH align="right">Actions</TH>
 *       </TR>
 *     </THead>
 *     <TBody>
 *       {rows.map((r) => (
 *         <TR key={r.id} hover>
 *           <TD>{r.name}</TD>
 *           <TD>{r.status}</TD>
 *           <TD align="right">…</TD>
 *         </TR>
 *       ))}
 *     </TBody>
 *   </Table>
 *
 * Pass `compact` on <Table> for the px-3 py-2 variant (dense tables).
 * Pass `hover` on each body <TR> to enable the row-hover background.
 */

import React, { createContext, useContext } from "react";
import { useTheme } from "../../context/ThemeContext";
import {
  getCardBackground,
  getLightBorder,
  getSecondaryText,
} from "../../utils/themeUtils";
import { TOKENS } from "../../utils/designTokens";

interface TableContextValue {
  compact: boolean;
}

const TableContext = createContext<TableContextValue>({ compact: false });

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  /** Switch to denser px-3 py-2 cell padding for data-heavy tables. */
  compact?: boolean;
  /** Wrap the table in an overflow-x-auto container. Default true. */
  scrollable?: boolean;
}

export const Table: React.FC<TableProps> = ({
  compact = false,
  scrollable = true,
  className = "",
  children,
  ...rest
}) => {
  const tableEl = (
    <table className={`${TOKENS.table.base} ${className}`} {...rest}>
      <TableContext.Provider value={{ compact }}>{children}</TableContext.Provider>
    </table>
  );

  return scrollable ? <div className="overflow-x-auto">{tableEl}</div> : tableEl;
};

export const THead: React.FC<React.HTMLAttributes<HTMLTableSectionElement>> = ({
  className = "",
  children,
  ...rest
}) => {
  const { isDarkMode } = useTheme();
  const bg = isDarkMode ? "bg-gray-700" : "bg-gray-50";
  return (
    <thead className={`${bg} ${TOKENS.table.header} ${getLightBorder(isDarkMode)} ${className}`} {...rest}>
      {children}
    </thead>
  );
};

export const TBody: React.FC<React.HTMLAttributes<HTMLTableSectionElement>> = ({
  className = "",
  children,
  ...rest
}) => {
  const { isDarkMode } = useTheme();
  return (
    <tbody className={`${getCardBackground(isDarkMode)} ${className}`} {...rest}>
      {children}
    </tbody>
  );
};

interface TRProps extends React.HTMLAttributes<HTMLTableRowElement> {
  /** Enable hover background for body rows. */
  hover?: boolean;
}

export const TR: React.FC<TRProps> = ({ hover = false, className = "", children, ...rest }) => {
  const { isDarkMode } = useTheme();
  const hoverClasses = hover
    ? isDarkMode
      ? "hover:bg-gray-700/50"
      : "hover:bg-gray-50"
    : "";
  return (
    <tr className={`${TOKENS.table.row} ${getLightBorder(isDarkMode)} ${hoverClasses} ${className}`} {...rest}>
      {children}
    </tr>
  );
};

interface CellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /** Text alignment. Defaults to left. */
  align?: "left" | "center" | "right";
}

export const TH: React.FC<CellProps> = ({ align = "left", className = "", children, ...rest }) => {
  const { isDarkMode } = useTheme();
  const { compact } = useContext(TableContext);
  const padding = compact ? TOKENS.table.compactCell : TOKENS.table.headerCell.replace(/text-left/, "");
  const alignClass =
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  return (
    <th
      className={`${padding} ${alignClass} font-medium uppercase tracking-wider text-xs ${getSecondaryText(isDarkMode)} ${className}`}
      {...rest}
    >
      {children}
    </th>
  );
};

export const TD: React.FC<CellProps> = ({ align = "left", className = "", children, ...rest }) => {
  const { compact } = useContext(TableContext);
  const padding = compact ? TOKENS.table.compactCell : TOKENS.table.cell;
  const alignClass =
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  return (
    <td className={`${padding} ${alignClass} ${className}`} {...rest}>
      {children}
    </td>
  );
};

const TableExports = { Table, THead, TBody, TR, TH, TD };
export default TableExports;
